/**
 * Shopify Flyout Cart
 * Handles open/close, AJAX add-to-cart, quantity updates, and item removal.
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-drawer-overlay');
    this.closeBtn = document.getElementById('cart-drawer-close');
    this.body = document.getElementById('cart-drawer-body');
    this.footer = document.getElementById('cart-drawer-footer');
    this.loading = document.getElementById('cart-drawer-loading');
    this.countEl = document.getElementById('cart-drawer-count');
    this.subtotalEl = document.getElementById('cart-drawer-subtotal');

    this.isOpen = false;
    this.init();
  }

  init() {
    // Open triggers – any element with [data-open-cart]
    document.querySelectorAll('[data-open-cart]').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
    });

    // Cart icon in header (common Shopify selectors)
    const headerCartLinks = document.querySelectorAll(
      'a[href="/cart"], .cart-link, .header__icon--cart, [data-cart-toggle]'
    );
    headerCartLinks.forEach(link => {
      link.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
    });

    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());

    const closeEmpty = document.getElementById('cart-drawer-close-empty');
    closeEmpty?.addEventListener('click', () => this.close());

    // Keyboard
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Quantity buttons & remove – delegated
    this.body?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const key = btn.dataset.key;
      const action = btn.dataset.action;

      if (action === 'remove') {
        this.updateItem(key, 0);
      } else if (action === 'increase' || action === 'decrease') {
        const input = this.body.querySelector(`.cart-item__quantity-input[data-key="${key}"]`);
        if (!input) return;
        const current = parseInt(input.value, 10);
        const newQty = action === 'increase' ? current + 1 : Math.max(0, current - 1);
        input.value = newQty;
        this.updateItem(key, newQty);
      }
    });

    // Manual quantity input (debounced)
    this.body?.addEventListener('change', (e) => {
      const input = e.target.closest('.cart-item__quantity-input');
      if (!input) return;
      const key = input.dataset.key;
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      this.updateItem(key, qty);
    });

    // Intercept add-to-cart forms
    document.querySelectorAll('form[action="/cart/add"]').forEach(form => {
      if (form.dataset.cartHandled) return;
      form.dataset.cartHandled = 'true';
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart(form);
      });
    });

    // Also catch dynamically added forms via MutationObserver
    const observer = new MutationObserver(() => {
      document.querySelectorAll('form[action="/cart/add"]:not([data-cart-handled])').forEach(form => {
        form.dataset.cartHandled = 'true';
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.addToCart(form);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    this.updateHeaderCount();
  }

  /* ─── Open / Close ─────────────────────────────────────── */

  open() {
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    this.isOpen = true;
    this.closeBtn?.focus();
  }

  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    this.isOpen = false;
  }

  /* ─── Loading State ─────────────────────────────────────── */

  setLoading(on) {
    this.loading?.setAttribute('aria-hidden', on ? 'false' : 'true');
    this.loading?.classList.toggle('is-visible', on);
  }

  /* ─── API Helpers ───────────────────────────────────────── */

  async request(url, options = {}) {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      ...options,
    });
    if (!res.ok) throw new Error(`Cart request failed: ${res.status}`);
    return res.json();
  }

  /* ─── Add to Cart ───────────────────────────────────────── */

  async addToCart(form) {
    this.setLoading(true);
    try {
      const formData = new FormData(form);
      const body = {};
      formData.forEach((v, k) => { body[k] = v; });

      await this.request('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      await this.refreshCart();
      this.open();
    } catch (err) {
      console.error('[CartDrawer] addToCart error:', err);
      this.showError('Could not add item. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  /* ─── Update / Remove Item ──────────────────────────────── */

  async updateItem(key, quantity) {
    this.setLoading(true);
    try {
      const updates = { [key]: quantity };
      const cart = await this.request('/cart/update.js', {
        method: 'POST',
        body: JSON.stringify({ updates }),
      });
      this.renderCart(cart);
    } catch (err) {
      console.error('[CartDrawer] updateItem error:', err);
      this.showError('Could not update cart. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  /* ─── Refresh Cart ──────────────────────────────────────── */

  async refreshCart() {
    const cart = await this.request('/cart.js');
    this.renderCart(cart);
  }

  /* ─── Render Cart ───────────────────────────────────────── */

  renderCart(cart) {
    this.updateHeaderCount(cart.item_count);

    const count = cart.item_count;
    const pluralLabel = count === 1 ? 'item' : 'items';
    if (this.countEl) this.countEl.textContent = `${count} ${pluralLabel}`;

    if (this.subtotalEl) {
      this.subtotalEl.textContent = this.formatMoney(cart.total_price);
    }

    if (count === 0) {
      this.body.innerHTML = this.emptyCartHTML();
      if (this.footer) this.footer.style.display = 'none';
      document.getElementById('cart-drawer-close-empty')?.addEventListener('click', () => this.close());
    } else {
      if (this.footer) this.footer.style.display = '';
      this.renderItems(cart.items);
    }
  }

  renderItems(items) {
    const list = this.body.querySelector('#cart-drawer-items') || this.createItemsList();

    list.innerHTML = items.map(item => this.itemHTML(item)).join('');
  }

  createItemsList() {
    this.body.innerHTML = `<ul class="cart-drawer__items" id="cart-drawer-items" role="list"></ul>`;
    return this.body.querySelector('#cart-drawer-items');
  }

  itemHTML(item) {
    const hasDiscount = item.original_line_price !== item.final_line_price;
    const imageHTML = item.image
      ? `<img src="${item.image}" alt="${item.title}" class="cart-item__image" width="60" height="60" loading="lazy">`
      : `<div class="cart-item__image cart-item__image--placeholder"></div>`;

    const variantHTML = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-item__variant">${item.variant_title}</p>` : '';

    const discountHTML = hasDiscount
      ? `<s class="cart-item__price cart-item__price--original">${this.formatMoney(item.original_line_price)}</s>` : '';

    return `
      <li class="cart-item" data-key="${item.key}">
        <a href="${item.url}" class="cart-item__image-link">${imageHTML}</a>
        <div class="cart-item__details">
          <a href="${item.url}" class="cart-item__name">${item.product_title}</a>
          ${variantHTML}
          <div class="cart-item__bottom">
            <div class="cart-item__quantity-wrapper">
              <button class="cart-item__quantity-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease">−</button>
              <input class="cart-item__quantity-input" type="number" value="${item.quantity}" min="0" data-key="${item.key}" aria-label="Quantity">
              <button class="cart-item__quantity-btn" data-action="increase" data-key="${item.key}" aria-label="Increase">+</button>
            </div>
            <div class="cart-item__price-wrapper">
              ${discountHTML}
              <span class="cart-item__price">${this.formatMoney(item.final_line_price)}</span>
            </div>
          </div>
        </div>
        <button class="cart-item__remove" data-action="remove" data-key="${item.key}" aria-label="Remove ${item.product_title}">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
        </button>
      </li>`;
  }

  emptyCartHTML() {
    return `
      <div class="cart-drawer__empty" id="cart-drawer-empty">
        <div class="cart-drawer__empty-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
        </div>
        <p class="cart-drawer__empty-title">Your cart is empty</p>
        <p class="cart-drawer__empty-subtitle">Add something to get started.</p>
        <a href="/collections/all" class="cart-drawer__continue-btn" id="cart-drawer-close-empty">Shop Now</a>
      </div>`;
  }

  /* ─── Header Count ──────────────────────────────────────── */

  updateHeaderCount(count) {
    if (count === undefined) {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => this.updateHeaderCount(cart.item_count));
      return;
    }
    document.querySelectorAll(
      '.cart-count, [data-cart-count], .header__cart-count, .cart-count-bubble'
    ).forEach(el => {
      el.textContent = count;
      el.setAttribute('aria-label', `${count} items in cart`);
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ─── Error Toast ───────────────────────────────────────── */

  showError(message) {
    const toast = Object.assign(document.createElement('div'), {
      className: 'cart-drawer__toast cart-drawer__toast--error',
      textContent: message,
    });
    this.drawer.appendChild(toast);
    setTimeout(() => toast.classList.add('is-visible'), 10);
    setTimeout(() => { toast.classList.remove('is-visible'); setTimeout(() => toast.remove(), 300); }, 3500);
  }

  /* ─── Money Formatter ───────────────────────────────────── */

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
    } catch {
      return `$${amount}`;
    }
  }
}

// Boot
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});
